import { useQuery } from '@tanstack/react-query';
import { Paperclip, ShieldCheck, ShieldX } from 'lucide-react';
import Modal from '../../components/ui/Modal';
import Spinner from '../../components/ui/Spinner';
import { getErrorMessage } from '../../lib/apiClient';
import { formatBytes, formatDate } from '../../utils/format';
import { getSecret, secretKeys } from './secretsApi';
import { sentTo } from './sentTo';

function StatusRow({ label, children }) {
  return (
    <div className="flex items-center justify-between gap-4 py-1.5 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="text-right font-medium text-slate-700">{children}</span>
    </div>
  );
}

/**
 * Sender-side view of a sent encrypted message: lifecycle status, attachment
 * metadata, and the access log. The message text itself is not shown — it is
 * end-to-end unrecoverable after sending (one-time reveal + crypto-shred).
 */
export default function MessageDetailModal({ open, onClose, secretId }) {
  const { data, isLoading, error } = useQuery({
    queryKey: secretKeys.detail(secretId),
    queryFn: () => getSecret(secretId),
    enabled: Boolean(open && secretId),
  });

  return (
    <Modal open={open} onClose={onClose} title={data?.name ?? 'Message details'}>
      {isLoading ? (
        <div className="py-8">
          <Spinner />
        </div>
      ) : error ? (
        <p className="py-4 text-sm text-red-600">
          {getErrorMessage(error, 'Could not load the message details')}
        </p>
      ) : data ? (
        <div className="space-y-5">
          <div className="divide-y divide-slate-200 rounded-lg border border-slate-200 px-3">
            <StatusRow label="Status">
              {data.destroyedAt ? (
                <span className="text-red-500">Destroyed {formatDate(data.destroyedAt)}</span>
              ) : data.revealedAt ? (
                <span className="text-emerald-600">Opened {formatDate(data.revealedAt)}</span>
              ) : (
                'Not opened yet'
              )}
            </StatusRow>
            <StatusRow label="Sent to">{sentTo(data)}</StatusRow>
            <StatusRow label="Views">
              {data.viewsCount}
              {data.maxViews ? ` / ${data.maxViews}` : ''}
              {data.oneTime ? ' (one-time)' : ''}
            </StatusRow>
            <StatusRow label="Created">{formatDate(data.createdAt)}</StatusRow>
            <StatusRow label="Expires">
              {data.expiresAt ? formatDate(data.expiresAt) : 'Never'}
            </StatusRow>
            {data.selfDestructAt && (
              <StatusRow label="Self-destructs">{formatDate(data.selfDestructAt)}</StatusRow>
            )}
          </div>

          <div>
            <p className="mb-1.5 text-sm font-medium text-slate-700">Attachments</p>
            {data.attachments.length === 0 ? (
              <p className="text-xs text-slate-400">
                {data.destroyedAt
                  ? 'Attachments are deleted when a message is destroyed.'
                  : 'No attachments.'}
              </p>
            ) : (
              <ul className="divide-y divide-slate-200 rounded-lg border border-slate-200">
                {data.attachments.map((a) => (
                  <li key={a.id} className="flex items-center gap-2 px-3 py-2 text-sm">
                    <Paperclip className="h-4 w-4 shrink-0 text-slate-400" />
                    <span className="flex-1 truncate text-slate-700">{a.name}</span>
                    <span className="text-xs text-slate-400">{formatBytes(a.sizeBytes)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <p className="mb-1.5 text-sm font-medium text-slate-700">Access log</p>
            {data.accessLog.length === 0 ? (
              <p className="text-xs text-slate-400">No activity recorded yet.</p>
            ) : (
              <ul className="max-h-56 divide-y divide-slate-200 overflow-y-auto rounded-lg border border-slate-200">
                {data.accessLog.map((l) => (
                  <li key={l.id} className="flex items-start gap-2 px-3 py-2 text-sm">
                    {l.success ? (
                      <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                    ) : (
                      <ShieldX className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
                    )}
                    <span className="min-w-0 flex-1">
                      <span className="block font-medium capitalize text-slate-700">
                        {String(l.event).replace(/_/g, ' ')}
                        {l.reason ? ` — ${l.reason}` : ''}
                      </span>
                      <span className="block truncate text-xs text-slate-400" title={l.userAgent ?? ''}>
                        {l.ip ?? 'unknown IP'}
                        {l.userAgent ? ` · ${l.userAgent}` : ''}
                      </span>
                    </span>
                    <span className="shrink-0 text-xs text-slate-400">{formatDate(l.createdAt)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <p className="text-xs text-slate-400">
            The message text is end-to-end encrypted and cannot be shown here — it is only
            decrypted for the recipient via the one-time link.
          </p>
        </div>
      ) : null}
    </Modal>
  );
}
